import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Plus, Users } from 'lucide-react';
import { getApiBaseUrl } from '../../config/api';
import AuthenticatedNavbar from '../headers/AuthenticatedNavbar';
import { isEmployer } from '../../utils/userRole';
import EventAttendancePanel from './EventAttendancePanel';
import { formatEventPlace, formatEventWhen, isEventClosed } from './calendarLinks';
import './EventAttendance.css';

export default function EventAttendanceList({ user, onLogout }) {
  const API_URL = getApiBaseUrl();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = user?.id ? `?user_id=${user.id}` : '';
      const res = await fetch(`${API_URL}/get_attendance_events.php${q}`);
      const data = await res.json();
      setEvents(data.success && Array.isArray(data.events) ? data.events : []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const coverUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_URL}/${String(path).replace(/^\/+/, '')}`;
  };

  return (
    <div className="eal-shell">
      {user && <AuthenticatedNavbar user={user} onLogout={onLogout} />}
      <div className="eal-page">
        <div className="eal-hero">
          <div className="eal-hero-copy">
            <span className="eal-kicker">
              <Calendar size={12} /> Community RSVP
            </span>
            <h1>Attend Events</h1>
            <p>RSVP to community events and save them to Google, Outlook, or Yahoo Calendar.</p>
            <div className="eal-hero-rule" aria-hidden="true" />
          </div>
          {isEmployer(user) && (
            <button type="button" className="eal-btn" onClick={() => setShowCreate(true)}>
              <Plus size={18} strokeWidth={2.5} />
              Create event
            </button>
          )}
        </div>

        {loading ? (
          <p className="eal-empty">Loading events…</p>
        ) : events.length === 0 ? (
          <div className="eal-empty">
            <strong>No events yet</strong>
            Check back soon for community gatherings you can attend.
          </div>
        ) : (
          <div className="eal-grid">
            {events.map((event) => {
              const going = Boolean(event.is_attending);
              const closed = isEventClosed(event);
              const place = formatEventPlace(event);
              return (
                <Link
                  key={event.id}
                  to={`/event-attendance/${event.id}`}
                  className="eal-card"
                >
                  <div className="eal-cover">
                    {event.cover_image ? (
                      <img src={coverUrl(event.cover_image)} alt="" />
                    ) : null}
                    {closed ? (
                      <span className="eal-cover-badge is-closed">Closed</span>
                    ) : going ? (
                      <span className="eal-cover-badge is-going">You’re going</span>
                    ) : null}
                  </div>
                  <div className="eal-body">
                    <h2>{event.title}</h2>
                    <div className="eal-meta">
                      <Calendar size={15} />
                      <span>{formatEventWhen(event)}</span>
                    </div>
                    {place && (
                      <div className="eal-meta">
                        <MapPin size={15} />
                        <span>{place}</span>
                      </div>
                    )}
                    <div className="eal-card-footer">
                      <span className={`eal-count${going ? ' is-going' : ''}`}>
                        <Users size={13} />
                        {Number(event.attendee_count || 0)} attending
                      </span>
                      <span className={`eal-attend-chip${closed ? ' is-closed' : going ? ' is-going' : ''}`}>
                        {closed ? 'Closed' : going ? 'View RSVP' : 'Attend Event'}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <div className="eal-modal-backdrop">
          <div className="eal-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button
                type="button"
                className="eal-modal-close"
                onClick={() => {
                  setShowCreate(false);
                  load();
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <EventAttendancePanel
              user={user}
              onClose={() => {
                setShowCreate(false);
                load();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
